import { Platform } from 'react-native';
import Constants from 'expo-constants';

export function readAloudBuildInfo() {
  let application, updates, device;
  if (Platform.OS === 'android' || Platform.OS === 'ios') {
    try {
      const { requireOptionalNativeModule } = require('expo');
      application = requireOptionalNativeModule('ExpoApplication');
      updates = requireOptionalNativeModule('ExpoUpdates');
      device = requireOptionalNativeModule('ExpoDevice');
    } catch (_) {}
  }
  return {
    platform: Platform.OS,
    appVersion: application?.nativeApplicationVersion || Constants.nativeAppVersion || null,
    appBuild: application?.nativeBuildVersion || Constants.nativeBuildVersion || null,
    runtimeVersion: updates?.runtimeVersion || Constants.expoRuntimeVersion || null,
    updateId: updates?.updateId || null,
    bundleId: process.env.EXPO_PUBLIC_BUILD_ID || null,
    deviceModel: device?.modelName || null,
    osVersion: device?.osVersion || String(Platform.Version || ''),
  };
}

// Never send source URLs, query tokens, chat text or an arbitrary error object.
export function playbackDiagnostic(error, includeDetails = false) {
  const clean = (value, limit) => String(value || '')
    .replace(/(?:https?:\/\/|file:\/\/|content:\/\/)[^\s)]+/gi, '[source]')
    .replace(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi, '[email]')
    .replace(/Bearer\s+[\w.-]+/gi, 'Bearer [redacted]')
    .replace(/(?:token|password|api[_-]?key|authorization)\s*[=:]\s*[^\s,;]+/gi, '[credential]')
    .slice(0, limit);
  return {
    ...readAloudBuildInfo(),
    code: ['audio_load_timeout', 'audio_start_timeout', 'audio_download_failed'].includes(error?.errorCode)
      ? error.errorCode : 'audio_playback_failed',
    ...(['remote_player', 'audio_download', 'android_media_player'].includes(error?.playbackStage)
      && { playbackStage: error.playbackStage }),
    ...(includeDetails && { message: clean(error?.message, 500), stack: clean(error?.stack, 4000) }),
  };
}

// Android's remote ExoPlayer path can fail while the same MP3 plays from disk.
// Retry transport once using the installed file-system API, never a new TTS job.
export async function downloadPlaybackAudio(url, signal) {
  if (Platform.OS !== 'android') throw new Error('Local audio fallback unavailable');
  if (signal?.aborted || !/^https?:\/\//i.test(url)) throw new Error('Audio download unavailable');
  const fs = require('expo-file-system');
  if (!fs.cacheDirectory) throw new Error('Audio cache unavailable');
  const uri = `${fs.cacheDirectory}read-aloud-${Date.now()}-${Math.random().toString(36).slice(2)}.mp3`;
  const dispose = () => fs.deleteAsync(uri, { idempotent: true }).catch(() => {});
  const request = fs.createDownloadResumable(url, uri);
  let timer, stopped = false, cancel;
  const cancelled = new Promise((_, reject) => {
    cancel = () => {
      stopped = true;
      request.pauseAsync().catch(() => {}).then(dispose);
      reject(new Error('Audio download stopped or timed out'));
    };
    timer = setTimeout(cancel, 30000);
  });
  signal?.addEventListener('abort', cancel);
  try {
    if (signal?.aborted) { cancel(); await cancelled; }
    const downloading = request.downloadAsync().then(async result => ({ result, info: await fs.getInfoAsync(uri) }));
    downloading.finally(() => { if (stopped) dispose(); }).catch(() => {});
    const { result, info } = await Promise.race([downloading, cancelled]);
    if (result.status < 200 || result.status >= 300 || !info.exists || info.size <= 0) {
      throw new Error('Audio download failed');
    }
    return { uri, dispose };
  } catch (error) {
    await dispose();
    error.errorCode = 'audio_download_failed';
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  }
}
